/**
 * useUpdateEltJob Hook
 *
 * React Query mutation hook for updating an ELT job.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { eltJobService } from '../../../api/elt-job.service';
import { ELT_JOBS_QUERY_KEY } from './useEltJobs';
import { ELT_JOB_DETAILS_QUERY_KEY } from './useEltJobDetails';
import type { EltJobUpdateDTO, EltJob } from '../../../types/elt-job.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseUpdateEltJobOptions {
  onSuccess?: (eltJob: EltJob) => void;
  onError?: (error: string) => void;
}

export function useUpdateEltJob(
  tenantId: string,
  eltJobId: string,
  options?: UseUpdateEltJobOptions
) {
  const queryClient = useQueryClient();

  return useMutation<EltJob, Error, EltJobUpdateDTO>({
    mutationFn: (data: EltJobUpdateDTO) =>
      eltJobService.updateEltJob(tenantId, eltJobId, data),
    onSuccess: (eltJob) => {
      queryClient.invalidateQueries({ queryKey: [ELT_JOBS_QUERY_KEY, tenantId] });
      queryClient.invalidateQueries({
        queryKey: [ELT_JOB_DETAILS_QUERY_KEY, tenantId, eltJobId],
      });
      options?.onSuccess?.(eltJob);
    },
    onError: (error) => {
      options?.onError?.(getErrorMessage(error));
    },
  });
}
